import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronRight, BadgeCheck, MapPin, Calendar, Users, Star, ArrowRight } from "lucide-react";
import { GoldButton, GhostButton } from "@/components/site/ui";
import { useFetch } from "@/lib/useFetch";
import { useLead } from "@/context/LeadContext";
import { useAuth } from "@/context/AuthContext";
import { inr, api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

const LABELS = { opportunities: "Opportunity", experts: "Expert", influencers: "Influencer", podcasts: "Podcast", events: "Event", brands: "Brand" };
const CTA = { opportunities: "View Opportunity", experts: "Connect", influencers: "Collaborate", podcasts: "Apply as Guest", events: "Book / Enquire", brands: "Connect With Brand" };

export default function MarketplaceDetail({ kind }) {
    const { slug } = useParams();
    const nav = useNavigate();
    const { openLead } = useLead();
    const { user } = useAuth();
    const [booking, setBooking] = useState(false);
    const { data: item, loading } = useFetch(`/mk/${kind}/${slug}`);

    const handleBook = async () => {
        if (!user) { nav("/login"); return; }
        setBooking(true);
        try {
            const { data } = await api.post("/bookings", { kind, slug, note: "" });
            toast.success(`Request sent! Booking ${data.booking_id}. A SuDigi executive will confirm shortly.`);
            nav("/account");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBooking(false); }
    };
    useSeo(item ? { title: `${item.title || item.name} | SuDigi`, description: item.about || item.bio || item.story || "" } : { title: "SuDigi" });

    if (loading) return <div className="min-h-[60vh] grid place-items-center text-cream-soft">Loading…</div>;
    if (!item) return <div className="min-h-[60vh] grid place-items-center text-cream-soft">Not found.</div>;

    const facts = {
        opportunities: [["Organiser", item.organiser], ["Location", item.location], ["Date", item.date], ["Audience", item.audience]],
        experts: [["Title", item.title], ["Experience", item.experience], ["Location", item.location], ["Mode", item.mode], ["Rating", `${item.rating} (${item.reviews})`]],
        influencers: [["Category", item.category], ["Followers", item.followers], ["Engagement", item.engagement], ["Audience", item.audience], ["Location", item.location]],
        podcasts: [["Host", item.host], ["Category", item.category], ["Audience", item.audience], ["Episodes", item.episodes], ["Mode", item.mode], ["Guests", item.guest_req]],
        events: [["Organiser", item.organiser], ["Date", item.date], ["Time", item.time], ["Location", item.location], ["Mode", item.mode], ["Audience", item.audience], ["Speakers", item.speakers]],
        brands: [["Industry", item.industry], ["Location", item.location], ["Followers", item.followers]],
    }[kind];
    const desc = item.about || item.bio || item.story;

    return (
        <section className="bg-teal-900 py-12 min-h-[70vh]">
            <div className="container-x">
                <nav className="flex items-center gap-1.5 text-xs text-cream-soft/70 mb-6">
                    <Link to={`/${kind}`} className="hover:text-gold capitalize">{kind}</Link><ChevronRight className="w-3 h-3" /><span className="text-cream">{item.title || item.name}</span>
                </nav>
                <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        <div className="rounded-3xl overflow-hidden h-72 sm:h-96"><img src={item.image} alt={item.title || item.name} className="w-full h-full object-cover" /></div>
                        <div className="mt-6 flex items-center gap-2">
                            {item.category && <span className="bg-teal-700 text-gold text-xs font-bold px-3 py-1 rounded-full uppercase">{item.category}</span>}
                            {item.verified && <span className="flex items-center gap-1 text-xs text-growth"><BadgeCheck className="w-4 h-4" /> Verified {LABELS[kind]}</span>}
                        </div>
                        <h1 className="font-serif text-4xl text-cream mt-3">{item.title || item.name}</h1>
                        {desc && <p className="mt-4 text-cream-soft leading-relaxed">{desc}</p>}
                    </div>
                    <aside>
                        <div className="card-teal rounded-2xl p-6 sticky top-24">
                            {item.fee != null && <div className="mb-4"><div className="text-xs text-cream-soft/60 uppercase">Starting fee</div><div className="font-mono font-bold text-2xl text-gold">{item.fee === 0 ? "Free" : inr(item.fee)}</div></div>}
                            <dl className="space-y-3">
                                {facts.filter(([, v]) => v).map(([k, v]) => (
                                    <div key={k} className="flex justify-between gap-3 text-sm"><dt className="text-cream-soft/60">{k}</dt><dd className="text-cream text-right">{v}</dd></div>
                                ))}
                            </dl>
                            {item.fee != null && item.fee > 0 && (
                                <div className="mt-4 rounded-xl bg-teal-800/60 border border-brandborder p-3 text-xs text-cream-soft">
                                    <div className="flex justify-between"><span>Service fee</span><span className="font-mono">{inr(item.fee)}</span></div>
                                    <div className="flex justify-between"><span>SuDigi commission (10%)</span><span className="font-mono">{inr(Math.round(item.fee * 0.1))}</span></div>
                                    <div className="flex justify-between text-growth font-semibold mt-1 pt-1 border-t border-brandborder"><span>Provider receives</span><span className="font-mono">{inr(Math.round(item.fee * 0.9))}</span></div>
                                </div>
                            )}
                            <GoldButton onClick={handleBook} disabled={booking} className="w-full mt-5" data-testid="mk-detail-cta">{CTA[kind]} <ArrowRight className="w-4 h-4" /></GoldButton>
                            <GhostButton onClick={() => openLead(item.title || item.name)} className="w-full mt-2" data-testid="mk-detail-talk">Talk to an Expert</GhostButton>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
}
